import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'

// 类型对齐 server `internal/vip/service.go` + httpapi 序列化(2026-05-20 fix)。
// spec: docs/api-list/user/vip-purchase.md §3 list_vip_plans / get_my_vip

export type VipTier = 'bronze' | 'silver' | 'gold' | 'platinum'

/** 套餐分类(spec 2026-06-10 vip-plan-type;vip_plan.plan_type) */
export type PlanType = 'user' | 'forum_creator' | 'dating_creator'

export const PLAN_TYPE_NAME: Record<PlanType, string> = {
  user: '用户套餐',
  forum_creator: '论坛创作套餐',
  dating_creator: '约爱创作套餐',
}

export const PLAN_TYPE_ORDER: PlanType[] = ['user', 'forum_creator', 'dating_creator']

export type VipPlan = {
  plan_id: string
  plan_type: PlanType
  tier: VipTier
  name: string
  /** ¥ 分单位整数(0.01 元);UI 展示需 ÷ 100 */
  price_cny_cents: number
  /** 金币单位;provider='coin' 时展示 */
  price_coin: number
  duration_days: number
  /** 业务标签;当前取值集合 {'hot'} → UI 显示"推荐";新增取值需同步 buy.tsx badge 渲染 */
  tag: string | null
}

export type MyVipSegment = {
  tier: VipTier
  plan_id: string
  plan_type: PlanType
  /** RFC3339 timestamptz */
  started_at: string
  /** RFC3339 timestamptz */
  expires_at: string
}

export type MyVip = {
  /** null = 普通用户(无 active 段);else 当前最高 tier */
  effective_tier: VipTier | null
  /** 当前最远 active 段 expires_at;effective_tier=null 时 null */
  effective_expires_at: string | null
  active_segments: MyVipSegment[]
  /** 2026-06-12:评论权益(后端 vipcheck.BenefitComment 单点计算) */
  can_comment: boolean
}

export function useVipPlans() {
  return useQuery({
    queryKey: ['vip', 'plans'] as const,
    // 2026-05-20 修:server 实际 endpoint(R-10 user/admin 共享读)
    queryFn: () => apiClient.get<{ plans: VipPlan[] }>('/gapi/public/vip/plans'),
    staleTime: 5 * 60_000,
  })
}

export function useMyVip(enabled: boolean) {
  return useQuery({
    queryKey: ['vip', 'me'] as const,
    enabled,
    // 2026-05-20 修:server 实际 endpoint
    queryFn: () => apiClient.get<MyVip>('/gapi/user/vip'),
    staleTime: 30_000,
  })
}

/** 派生 helper:effective_tier 非空即视作 VIP(替代旧 `> 0` 错误数值比较) */
export function isVipUser(my: MyVip | undefined): boolean {
  return !!my && my.effective_tier !== null
}

export const TIER_NAMES = ['普通', '青铜VIP', '白银VIP', '黄金VIP', '铂金VIP']

// server 用 string enum,UI 旧组件按索引 1-4 取色取名(0 = 普通);转一遍。
const TIER_INDEX: Record<VipTier, 1 | 2 | 3 | 4> = {
  bronze: 1,
  silver: 2,
  gold: 3,
  platinum: 4,
}

export function tierToIndex(tier: VipTier | null | undefined): 0 | 1 | 2 | 3 | 4 {
  return tier ? TIER_INDEX[tier] : 0
}
